import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback, useMemo } from "react";
import Layout from '../../Layout'
import ConnectModal from './ConnectModal'
import './styles.scss'
import ScrollTabs from "./Tab";
import {
    Input,
    Button,
    Skeleton,
    Tooltip
} from "antd";
import {
    ReloadOutlined,
    PlusOutlined,
    AppstoreOutlined,
    SearchOutlined,
    TeamOutlined,
    InboxOutlined
} from "@ant-design/icons";
import ConnectGroupModal from "./ConnectGroupModal";
import { Link, useNavigate } from "react-router-dom";
import { getChannelConnections, ChannelConnectionItem } from "../../api/ChannelConnection";
import { getChannelGroups, getChannelGroupById } from "../../api/ChannelGroup";
import {
    CHANNEL_TYPE_ORDER,
    canonicalChannelCode,
    getChannelImage,
    getChannelLabel
} from "./channelTypes";

interface GroupTab {
    id: number;
    name: string;
    connectionIds: number[];
}

// Bỏ dấu tiếng Việt để tìm "vien tham my" vẫn ra "Viện Thẩm Mỹ"
const normalize = (s: string) =>
    s.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D").toLowerCase().trim();

const typeOrder = (code: string) => {
    const idx = CHANNEL_TYPE_ORDER.indexOf(code);
    return idx === -1 ? CHANNEL_TYPE_ORDER.length : idx;
};

export default function DashboardPage() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("all");
    const [openModal, setOpenModal] = useState(false);
    const [openModalGroup, setOpenModalGroup] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [connections, setConnections] = useState<ChannelConnectionItem[]>([]);
    const [channelGroups, setChannelGroups] = useState<GroupTab[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const navigate = useNavigate();

    // Tải toàn bộ kênh một lần (tối đa 200); tìm kiếm và lọc tab làm phía client
    // để không gọi API mỗi lần gõ phím và số đếm trên tab luôn đúng
    const fetchConnections = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getChannelConnections({ page: 1, limit: 200 });
            if (res.status && res.data) {
                // Sắp xếp kênh có nhiều khách hàng lên trước cho dễ theo dõi
                const sorted = [...res.data.items].sort(
                    (a, b) => (b.total_channel_customers || 0) - (a.total_channel_customers || 0)
                );
                setConnections(sorted);
                setLoadError(false);
            } else {
                setLoadError(true);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // Load nhóm kênh để nối tiếp vào thanh tab; cần connectionIds của từng nhóm
    // để lọc card, nếu API list không trả kèm connections thì gọi chi tiết
    const fetchGroups = useCallback(async () => {
        const res = await getChannelGroups({ limit: 100 });
        if (!res.status) return;
        const groups = await Promise.all(
            res.items.map(async (g): Promise<GroupTab> => {
                if (Array.isArray(g.connections)) {
                    return { id: g.id, name: g.name, connectionIds: g.connections.map(c => c.id) };
                }
                const detail = await getChannelGroupById(g.id);
                return {
                    id: g.id,
                    name: g.name,
                    connectionIds: (detail.data?.connections || []).map(c => c.id),
                };
            })
        );
        setChannelGroups(groups);
    }, []);

    useEffect(() => {
        fetchConnections();
        fetchGroups();
    }, [fetchConnections, fetchGroups]);

    // Lọc theo từ khoá trước, số đếm trên tab phản ánh kết quả tìm kiếm
    const searched = useMemo(() => {
        const keyword = normalize(searchValue);
        if (!keyword) return connections;
        return connections.filter((c) =>
            normalize(`${c.display_name || ""} ${c.external_id || ""}`).includes(keyword)
        );
    }, [connections, searchValue]);

    const tabs = useMemo(() => {
        const channelCounts = searched.reduce<Record<string, number>>((acc, item) => {
            const code = canonicalChannelCode(item.channel_type_code);
            acc[code] = (acc[code] || 0) + 1;
            return acc;
        }, {});
        // Giữ tab đang chọn trên thanh tab dù tìm kiếm không còn kết quả, tránh mất dấu đang ở tab nào
        if (activeTab !== "all" && !activeTab.startsWith("group-") && !channelCounts[activeTab]) {
            channelCounts[activeTab] = 0;
        }

        return [
            { key: "all", name: t("chats.tab_all"), count: searched.length, img: "https://pancake.vn/static/images/platform_all.jpg" },
            // Nhóm kênh (chế độ gộp trang) đưa lên đầu, ngay sau "Tất cả" để dễ đọc, không bị che khuất ở cuối
            ...channelGroups.map((g) => ({
                key: `group-${g.id}`,
                name: g.name,
                count: g.connectionIds.length,
                icon: <TeamOutlined />,
            })),
            ...Object.entries(channelCounts)
                .sort(([a], [b]) => typeOrder(a) - typeOrder(b))
                .map(([code, count]) => ({
                    key: code,
                    name: getChannelLabel(code),
                    count,
                    img: getChannelImage(code),
                })),
        ];
    }, [searched, channelGroups, activeTab, t]);

    const filteredConnections = activeTab === "all"
        ? searched
        : searched.filter(c => canonicalChannelCode(c.channel_type_code) === activeTab);

    const totalCustomers = connections.reduce((sum, c) => sum + (c.total_channel_customers || 0), 0);

    const handleTabChange = (tabKey: string) => {
        // Tab nhóm kênh: vào thẳng phòng chat gộp trang của nhóm
        if (tabKey.startsWith("group-")) {
            navigate(`/chat/${tabKey.replace("group-", "")}?group=true`);
            return;
        }
        setActiveTab(tabKey);
    };

    const handleReload = () => {
        fetchConnections();
        fetchGroups();
    };

    const renderEmpty = () => (
        <div className="col-span-full flex flex-col items-center justify-center py-16 rounded-xl bg-white dark:bg-[#1f2937] border border-dashed border-gray-200 dark:border-gray-700 text-center">
            <InboxOutlined className="text-4xl text-gray-300" />
            {loadError ? (
                <>
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{t("chats.load_failed", "Không tải được danh sách kênh.")}</p>
                    <Button className="mt-3" icon={<ReloadOutlined />} onClick={handleReload}>
                        {t("chats.retry", "Thử lại")}
                    </Button>
                </>
            ) : searchValue.trim() ? (
                <>
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                        {t("chats.no_result", "Không tìm thấy kênh nào khớp")} “{searchValue.trim()}”
                    </p>
                    <Button className="mt-3" onClick={() => setSearchValue("")}>
                        {t("chats.clear_search", "Xoá tìm kiếm")}
                    </Button>
                </>
            ) : (
                <>
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{t("chats.no_connection", "Chưa có kênh nào được kết nối.")}</p>
                    <Button
                        className="mt-3 bg-[#3e91ab] hover:!bg-[#357f96] !text-white border-none"
                        icon={<PlusOutlined />}
                        onClick={() => setOpenModal(true)}
                    >
                        {t("chats.connect")}
                    </Button>
                </>
            )}
        </div>
    );

    return (
        <Layout>
            <Helmet>
                <title>{t('page.chats')} | Khothi</title>
            </Helmet>
            <div>
                {/* HEADER */}
                <div className="rounded-xl p-4 t-bg-white border border-gray-200 transition-colors duration-300 mb-4">
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 min-w-0">
                            <div className="shrink-0">
                                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 leading-tight">
                                    {t('chats.dashboard')}
                                </h2>
                                {!loading && !loadError && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {connections.length.toLocaleString('vi-VN')} {t('chats.channels', 'kênh')} · {totalCustomers.toLocaleString('vi-VN')} {t('chats.customers')}
                                    </p>
                                )}
                            </div>
                            {/* SEARCH */}
                            <div className="w-full sm:w-[320px] sm:ml-4">
                                <Input
                                    allowClear
                                    prefix={<SearchOutlined className="text-gray-400" />}
                                    placeholder={t('chats.search_channel', 'Tìm theo tên trang hoặc ID')}
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    className="t-input bg-gray-100 h-[34px]"
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 shrink-0">
                            <Tooltip title={t('chats.reload', 'Tải lại')}>
                                <Button
                                    icon={<ReloadOutlined spin={loading} />}
                                    onClick={handleReload}
                                    disabled={loading}
                                    aria-label={t('chats.reload', 'Tải lại')}
                                    className="bg-gray-100 dark:bg-gray-700 t-text-white border-none dark:!text-[#357f96]"
                                />
                            </Tooltip>

                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => setOpenModal(true)}
                                className="bg-[#3e91ab] hover:!bg-[#357f96] hover:!text-white text-white border-none"
                            >
                                {t('chats.connect')}
                            </Button>

                            <Button
                                icon={<AppstoreOutlined />}
                                onClick={() => setOpenModalGroup(true)}
                                className="bg-gray-100 dark:bg-gray-700 dark:text-[#357f96] border-none hover:!text-white hover:!bg-[#3e91ab]"
                            >
                                {t('chats.merge_page')}
                            </Button>
                        </div>
                    </div>
                </div>

                <ConnectModal
                    open={openModal}
                    onClose={() => {
                        setOpenModal(false);
                        // Có thể vừa kết nối thêm kênh
                        fetchConnections();
                    }}
                />
                <ConnectGroupModal
                    open={openModalGroup}
                    onClose={() => {
                        setOpenModalGroup(false);
                        fetchGroups();
                    }}
                />

                {/* TABS */}
                <ScrollTabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onChange={handleTabChange}
                />

                {/* CARDS */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3 md:gap-4">

                    {loading && connections.length === 0
                        ? Array.from({ length: 8 }).map((_, idx) => (
                            <div
                                key={`skeleton-${idx}`}
                                className="p-4 rounded-xl bg-white dark:bg-[#1f2937] border border-gray-200 dark:border-gray-700"
                            >
                                <div className="flex items-center gap-3">
                                    <Skeleton.Avatar active size={56} shape="square" />
                                    <div className="flex-1">
                                        <Skeleton active title={false} paragraph={{ rows: 3, width: ['80%', '60%', '50%'] }} />
                                    </div>
                                </div>
                            </div>
                        ))
                        : filteredConnections.length === 0
                            ? renderEmpty()
                            : filteredConnections.map((conn) => {
                                const inactive = conn.status === 0;
                                return (
                                    <Link
                                        key={conn.id}
                                        to={`/chat/${conn.id}`}
                                        className={`
                                            group relative block p-4 rounded-xl
                                            bg-white dark:bg-[#1f2937]
                                            border border-gray-200 dark:border-gray-700
                                            hover:shadow-md hover:border-[#3e91ab]/50
                                            focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3e91ab]
                                            transition-all duration-200
                                        `}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="relative w-14 h-14 flex-shrink-0">
                                                <img
                                                    src={conn.avatar_url || getChannelImage(conn.channel_type_code)}
                                                    alt={conn.display_name}
                                                    loading="lazy"
                                                    className={`w-full h-full rounded-lg object-cover bg-gray-100 dark:bg-gray-700 ${inactive ? "grayscale opacity-60" : ""}`}
                                                    onError={(e) => {
                                                        const target = e.currentTarget;
                                                        const fallback = getChannelImage(conn.channel_type_code);
                                                        if (target.src !== fallback) {
                                                            target.src = fallback;
                                                        }
                                                    }}
                                                />
                                                {/* Logo nền tảng góc avatar (bỏ qua khi avatar đã là logo nền tảng) */}
                                                {conn.avatar_url && <img
                                                    src={getChannelImage(conn.channel_type_code)}
                                                    alt={getChannelLabel(conn.channel_type_code)}
                                                    className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full object-cover bg-white ring-2 ring-white dark:ring-[#1f2937]"
                                                />}
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <p
                                                    className="font-medium text-gray-800 dark:text-gray-100 text-sm truncate group-hover:text-[#3e91ab] transition-colors"
                                                    title={conn.display_name}
                                                >
                                                    {conn.display_name}
                                                </p>

                                                <p className="text-[12px] mt-0.5 text-gray-500 dark:text-gray-400 truncate" title={conn.external_id}>
                                                    {getChannelLabel(conn.channel_type_code)} · {conn.external_id}
                                                </p>

                                                <div className="flex items-center justify-between gap-2 mt-1">
                                                    <div className="flex items-center gap-1 text-[12px] text-[#22c55e]">
                                                        <TeamOutlined />
                                                        <span className="font-medium">
                                                            {(conn.total_channel_customers || 0).toLocaleString('vi-VN')}
                                                        </span>
                                                        <span className="text-gray-400">{t('chats.customers')}</span>
                                                    </div>
                                                    {inactive && (
                                                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 whitespace-nowrap">
                                                            {t('chats.inactive', 'Không hoạt động')}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}

                </div>
            </div>
        </Layout>
    );
}
